import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { finalize, interval, Subscription, timeout } from 'rxjs';
import { BreBEstadoEnum } from '@core/models/states.enum';
import { BrebService } from '@core/services/api-services.index';
import { ConnectionStatus, WebsocketService } from '@core/services/websocket.service';
import { deferViewUpdate } from '@core/utils/defer-view-update';

@Component({
  selector: 'checkout-bre-b-form-container',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatSnackBarModule],
  templateUrl: './checkout-bre-b-form-container.component.html',
  styleUrl: './checkout-bre-b-form-container.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CheckoutBreBFormContainerComponent implements OnInit, OnDestroy {
  estado: BreBEstadoEnum = BreBEstadoEnum.SELECTION;
  estadoModel = BreBEstadoEnum;
  selectedMethod: 'bre-b' | 'tarjeta' | null = null;
  selectedBreBMethod: 'llave' | 'qr' | null = null;
  transactionId!: string;
  verification!: string;
  operationId?: number;
  key: string = '@safebuy';
  value: string = '$178,638.88';
  qrImageSrc: string = './assets/img/bre-b-qr-placeholder.png';
  timer: string = '13h : 20m : 15s';
  expirationDate = '';
  private okUrl = '';
  private errorUrl = '';
  private timerSubscription?: Subscription;
  private qrToLoadingTimeout?: ReturnType<typeof setTimeout>;
  private loadingToSuccessTimeout?: ReturnType<typeof setTimeout>;
  private readonly subscriptions = new Subscription();
  private websocketListenerId?: number;
  private pingInterval?: ReturnType<typeof setInterval>;
  private socketInitialized = false;
  private socketFlowFinished = false;

  private voucherShownWithoutBreBMethodSelection = false;
  private lastOperationAttributes: any[] = [];
  isGeneratingMethod = false;
  isInitialLoading = true;

  get showStandardProcessingView(): boolean {
    return (
      this.estado === BreBEstadoEnum.LOADING ||
      (this.estado === BreBEstadoEnum.BREB_METHOD_SELECTION && this.isGeneratingMethod)
    );
  }

  /** Texto del vencimiento en calendario Colombia (pantalla expirado). */
  get expirationFormattedColombia(): string {
    if (!this.expirationDate?.trim()) return '';
    const ms = this.parseOperationDateColombiaToEpochMs(this.expirationDate);
    if (ms === null) return '';
    return new Intl.DateTimeFormat('es-CO', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: 'America/Bogota',
    }).format(ms);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Inject(BrebService) private brebService: BrebService,
    private websocketService: WebsocketService
  ) {}

  private isVoucherViewState(): boolean {
    return this.estado === BreBEstadoEnum.PENDING || this.estado === BreBEstadoEnum.QR;
  }

  ngOnInit(): void {
    this.transactionId = this.route.snapshot.paramMap.get('transactionId') ?? '';
    this.verification = this.route.snapshot.queryParamMap.get('verification') ?? '';

    if (!this.transactionId || !this.verification) {
      deferViewUpdate(this.cdr, () => {
        this.isInitialLoading = false;
        this.snackBar.open('No se pudo obtener la operación.', '', { duration: 5000 });
      });
      return;
    }

    this.brebService
      .getOperation(this.transactionId, this.verification)
      .pipe(timeout(30_000))
      .subscribe({
        next: (data) => {
          deferViewUpdate(this.cdr, () => {
            try {
              const payload = this.unwrapOperationFindResponse(data);
              this.preloadFromOperation(payload);
              this.ensureSocketTracking();
            } catch (e) {
              console.error('BreB preload error:', e);
              this.snackBar.open('No se pudo procesar la respuesta del pago.', '', { duration: 5000 });
            } finally {
              this.isInitialLoading = false;
            }
          });
        },
        error: (error) => {
          console.error('BreB getOperation error:', error);
          deferViewUpdate(this.cdr, () => {
            this.isInitialLoading = false;
            this.snackBar.open('No se pudo obtener la operación.', '', { duration: 5000 });
          });
        },
      });
  }

  /** El backend a veces envuelve el body en `data` / `result` / `content`. */
  private unwrapOperationFindResponse(raw: unknown): any {
    if (raw == null || typeof raw !== 'object') {
      return raw;
    }
    const o = raw as Record<string, unknown>;
    if (o['operation'] != null) {
      return o;
    }
    const nested =
      (o['data'] as Record<string, unknown> | undefined) ??
      (o['result'] as Record<string, unknown> | undefined) ??
      (o['content'] as Record<string, unknown> | undefined);
    if (nested && typeof nested === 'object' && nested['operation'] != null) {
      return nested;
    }
    return o;
  }

  private ensureSocketTracking(): void {
    if (!this.operationId || this.socketInitialized || this.socketFlowFinished) return;

    this.socketInitialized = true;
    this.subscriptions.add(
      this.websocketService.getStatus().subscribe((connectionStatus) => {
        if (connectionStatus === ConnectionStatus.Connected && this.operationId) {
          this.websocketService.sendMessage({
            topic: 'identifierVoucher',
            data: { idOperation: this.operationId },
          });
        }
      })
    );

    this.websocketListenerId = this.websocketService.addMessageListener((message) => {
      if (message?.topic !== 'voucher-finished' || !message?.data) return;
      this.applySocketStatus(String(message.data.status ?? ''));
    });

    this.pingInterval = setInterval(() => {
      if (!this.socketFlowFinished) {
        this.websocketService.sendMessage({ topic: 'ping' });
      }
    }, 15000);
  }

  private applySocketStatus(status: string): void {
    const update = (fn: () => void) => deferViewUpdate(this.cdr, fn);

    if (status === '2') {
      if (
        this.estado === BreBEstadoEnum.APPROVED ||
        this.estado === BreBEstadoEnum.REFUSED ||
        this.estado === BreBEstadoEnum.EXPIRED
      ) {
        return;
      }

      if (this.voucherShownWithoutBreBMethodSelection && this.isVoucherViewState()) {
        return;
      }

      update(() => {
        if (this.selectedBreBMethod === 'qr') {
          this.estado = BreBEstadoEnum.QR;
        } else {
          this.estado = BreBEstadoEnum.PENDING;
        }
      });
      return;
    }

    if (status === '4') {
      if (this.voucherShownWithoutBreBMethodSelection && this.isVoucherViewState()) {
        return;
      }

      if (this.isGeneratingMethod || this.estado === BreBEstadoEnum.BREB_METHOD_SELECTION) {
        update(() => {
          this.estado = BreBEstadoEnum.LOADING;
        });
      }
      return;
    }

    if (status === '3') {
      return;
    }

    if (status === '5') {
      update(() => {
        this.stopExpirationTimer();
        this.estado = BreBEstadoEnum.APPROVED;
        this.finishSocketFlow();
      });
      return;
    }

    if (status === '6') {
      update(() => {
        this.stopExpirationTimer();
        this.estado = BreBEstadoEnum.REFUSED;
        this.finishSocketFlow();
      });
    }
  }

  private finishSocketFlow(): void {
    if (this.socketFlowFinished) return;
    this.socketFlowFinished = true;
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    this.websocketService.closeConnection();
  }

  /** Parsea `qr_code` del backend (JSON array con data URL). Devuelve true si se asignó imagen. */
  private tryApplyQrImageFromRaw(qrCodeRaw: string | undefined): boolean {
    if (!qrCodeRaw || typeof qrCodeRaw !== 'string') return false;
    try {
      const parsedQr = JSON.parse(qrCodeRaw);
      const qrValue = Array.isArray(parsedQr) ? parsedQr[0] : '';
      if (typeof qrValue === 'string' && qrValue.trim()) {
        this.qrImageSrc = qrValue;
        return true;
      }
    } catch {
      return false;
    }
    return false;
  }

  /**
   * Si el backend ya trae `qr_code` o `paymentCode`, muestra el voucher correspondiente.
   * Misma prioridad que al cargar la operación (QR antes que llave).
   */
  private applyExistingBreBVoucherFromAttributes(attributes: any[]): boolean {
    const qrCodeAttr = attributes.find((a: any) => a?.code === 'qr_code')?.value;
    const paymentCodeAttr = attributes.find((a: any) => a?.code === 'paymentCode')?.value;

    if (qrCodeAttr && this.tryApplyQrImageFromRaw(qrCodeAttr)) {
      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = 'qr';
      this.voucherShownWithoutBreBMethodSelection = true;
      this.estado = BreBEstadoEnum.QR;
      this.ensureSocketTracking();
      return true;
    }

    if (paymentCodeAttr) {
      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = 'llave';
      this.key = paymentCodeAttr;
      this.voucherShownWithoutBreBMethodSelection = true;
      this.estado = BreBEstadoEnum.PENDING;
      this.ensureSocketTracking();
      return true;
    }

    return false;
  }

  private upsertLastOperationAttribute(code: string, value: string | undefined): void {
    if (value == null || value === '') return;
    const list = this.lastOperationAttributes;
    const i = list.findIndex((a: any) => a?.code === code);
    const row = { code, value };
    if (i >= 0) {
      list[i] = { ...list[i], ...row };
    } else {
      list.push(row);
    }
  }

  private preloadFromOperation(response: any): void {
    const operation = response?.operation;
    const attributes = Array.isArray(response?.attributes) ? response.attributes : [];
    this.lastOperationAttributes = attributes;
    const rawId = operation?.id;
    if (rawId != null && rawId !== '') {
      const n = typeof rawId === 'number' ? rawId : Number(String(rawId).trim());
      if (!Number.isNaN(n)) {
        this.operationId = n;
      }
    }

    try {
    const processorName = response?.processor?.name;
    const channelListRaw = attributes.find((a: any) => a?.code === 'channel_list')?.value;

    if (operation?.currencySymbol && operation?.amount) {
      this.value = `${operation.currencySymbol}${operation.amount}`;
    }

    if (operation?.okUrl) {
      this.okUrl = operation.okUrl;
    }
    if (operation?.errorUrl) {
      this.errorUrl = operation.errorUrl;
    }

    const paymentCode = attributes.find((a: any) => a?.code === 'paymentCode')?.value;
    if (paymentCode) {
      this.key = paymentCode;
    }

    let paymentMethods: string[] = [];
    if (channelListRaw) {
      try {
        const parsedChannelList = JSON.parse(channelListRaw);
        paymentMethods = Array.isArray(parsedChannelList?.paymentMethods)
          ? parsedChannelList.paymentMethods
          : [];
      } catch {
        paymentMethods = [];
      }
    }

    const hasQrChannel = paymentMethods.includes('QR');
    const hasBankTransferChannel = paymentMethods.includes('BankTransfer');

    // Ya generaron QR o llave (refresh): ir directo al voucher; ← atrás → Bre-B / Tarjeta.
    if (this.applyExistingBreBVoucherFromAttributes(attributes)) {
      return;
    }

    // Si el backend informa ambos canales, todavía no hay selección de método:
    // mostrar pantalla de selección QR / Keys.
    if (hasQrChannel && hasBankTransferChannel) {
      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = null;
      this.voucherShownWithoutBreBMethodSelection = false;
      this.estado = BreBEstadoEnum.BREB_METHOD_SELECTION;
      return;
    }

    if (processorName === 'BreB_qr') {
      const qrCodeRaw = attributes.find((a: any) => a?.code === 'qr_code')?.value;
      if (qrCodeRaw) {
        this.tryApplyQrImageFromRaw(qrCodeRaw);
      }

      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = 'qr';
      this.voucherShownWithoutBreBMethodSelection = true;
      this.estado = BreBEstadoEnum.QR;
      this.ensureSocketTracking();
      return;
    }

    if (processorName === 'BreB_bt') {
      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = 'llave';
      this.voucherShownWithoutBreBMethodSelection = true;
      this.estado = BreBEstadoEnum.PENDING;
      this.ensureSocketTracking();
      return;
    }

    // Fallback por channel_list cuando no venga processor específico
    if (hasQrChannel && !hasBankTransferChannel) {
      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = 'qr';
      this.voucherShownWithoutBreBMethodSelection = true;
      this.estado = BreBEstadoEnum.QR;
      this.ensureSocketTracking();
      return;
    }

    if (!hasQrChannel && hasBankTransferChannel) {
      this.selectedMethod = 'bre-b';
      this.selectedBreBMethod = 'llave';
      this.voucherShownWithoutBreBMethodSelection = true;
      this.estado = BreBEstadoEnum.PENDING;
      this.ensureSocketTracking();
    }
    } finally {
      this.scheduleExpirationIfNeeded(operation);
    }
  }

  
  private scheduleExpirationIfNeeded(operation: any): void {
    if (!operation?.expirationDate) return;
    this.expirationDate = operation.expirationDate;
    const ms = this.parseOperationDateColombiaToEpochMs(this.expirationDate);
    if (ms === null) return;
    if (ms <= Date.now()) {
      this.timer = '00h : 00m : 00s';
      this.stopExpirationTimer();
      this.transitionToExpiredFromTimer();
      return;
    }
    this.startTimer(this.expirationDate);
  }

  selectMethod(method: 'bre-b' | 'tarjeta'): void {
    this.selectedMethod = method;
    if (method === 'tarjeta') {
      this.selectedBreBMethod = null;
      this.voucherShownWithoutBreBMethodSelection = false;
    }
  }

  onContinue(): void {
    if (!this.selectedMethod) return;

    if (this.selectedMethod === 'bre-b') {
      // Tras volver del voucher: reentrar al mismo QR o llave (memoria o attributes).
      if (this.selectedBreBMethod === 'qr') {
        this.voucherShownWithoutBreBMethodSelection = true;
        this.estado = BreBEstadoEnum.QR;
        return;
      }
      if (this.selectedBreBMethod === 'llave') {
        this.voucherShownWithoutBreBMethodSelection = true;
        this.estado = BreBEstadoEnum.PENDING;
        return;
      }
      if (this.applyExistingBreBVoucherFromAttributes(this.lastOperationAttributes)) {
        return;
      }
      this.selectedBreBMethod = null;
      this.voucherShownWithoutBreBMethodSelection = false;
      this.estado = BreBEstadoEnum.BREB_METHOD_SELECTION;
      return;
    }

 
    this.estado = BreBEstadoEnum.PENDING;
  }

  selectBreBMethod(method: 'llave' | 'qr'): void {
    this.selectedBreBMethod = method;
  }

  onContinueBreB(): void {
    if (!this.selectedBreBMethod || !this.operationId || this.isGeneratingMethod) return;

    this.isGeneratingMethod = true;

    if (this.selectedBreBMethod === 'llave') {
      this.brebService
        .createBankTransfer(this.operationId, this.verification)
        .pipe(
          timeout(30_000),
          finalize(() => {
            this.isGeneratingMethod = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (response) => {
            deferViewUpdate(this.cdr, () => {
              const keyCode = response?.keyCode;
              if (keyCode) {
                this.key = keyCode;
                this.upsertLastOperationAttribute('paymentCode', keyCode);
              }
              this.voucherShownWithoutBreBMethodSelection = true;
              this.estado = BreBEstadoEnum.PENDING;
              this.ensureSocketTracking();
            });
          },
          error: (error) => {
            console.error('BreB createBankTransfer error:', error);
            this.snackBar.open('No se pudo preparar el pago con llave. Puedes intentar de nuevo.', '', {
              duration: 5000,
            });
          },
        });
    } else {
      this.brebService
        .createQr(this.operationId, this.verification)
        .pipe(
          timeout(30_000),
          finalize(() => {
            this.isGeneratingMethod = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (response) => {
            deferViewUpdate(this.cdr, () => {
              const keyCode = response?.keyCode;
              if (keyCode) {
                this.key = keyCode;
              }

              const qrCodeRaw = response?.qrCode;
              if (qrCodeRaw) {
                this.tryApplyQrImageFromRaw(qrCodeRaw);
                this.upsertLastOperationAttribute('qr_code', qrCodeRaw);
              }

              this.clearQrFlowTimeouts();
              this.voucherShownWithoutBreBMethodSelection = true;
              this.estado = BreBEstadoEnum.QR;
              this.ensureSocketTracking();
            });
          },
          error: (error) => {
            console.error('BreB createQr error:', error);
            this.snackBar.open('No se pudo generar el código QR. Puedes intentar de nuevo.', '', {
              duration: 5000,
            });
          },
        });
    }
  }

  private clearQrFlowTimeouts(): void {
    if (this.qrToLoadingTimeout) {
      clearTimeout(this.qrToLoadingTimeout);
      this.qrToLoadingTimeout = undefined;
    }
    if (this.loadingToSuccessTimeout) {
      clearTimeout(this.loadingToSuccessTimeout);
      this.loadingToSuccessTimeout = undefined;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    if (this.websocketListenerId !== undefined) {
      this.websocketService.removeMessageListener(this.websocketListenerId);
      this.websocketListenerId = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    this.websocketService.closeConnection();
    this.clearQrFlowTimeouts();
  }

  
  private parseOperationDateColombiaToEpochMs(raw: string): number | null {
    const v = raw?.trim();
    if (!v) return null;
    const normalized = v.replace(' ', 'T');
    const hasExplicitOffset = /[+-]\d{2}:\d{2}(:\d{2})?$/.test(normalized);
    let s: string;
    if (/Z$/i.test(normalized)) {
      s = normalized.replace(/Z$/i, '-05:00');
    } else if (!hasExplicitOffset) {
      s = `${normalized}-05:00`;
    } else {
      s = normalized;
    }
    const ms = Date.parse(s);
    return Number.isNaN(ms) ? null : ms;
  }

  private stopExpirationTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }
  }


  private transitionToExpiredFromTimer(): void {
    if (
      this.estado === BreBEstadoEnum.APPROVED ||
      this.estado === BreBEstadoEnum.REFUSED ||
      this.estado === BreBEstadoEnum.EXPIRED
    ) {
      return;
    }
    this.estado = BreBEstadoEnum.EXPIRED;
    this.finishSocketFlow();
  }

  
  startTimer(expirationDateString: string): void {
    this.stopExpirationTimer();

    const expirationMs = this.parseOperationDateColombiaToEpochMs(expirationDateString);
    if (expirationMs === null) return;

    const tick = () => {
      const diffMs = expirationMs - Date.now();

      if (diffMs <= 0) {
        this.timer = '00h : 00m : 00s';
        this.stopExpirationTimer();
        this.transitionToExpiredFromTimer();
        return;
      }

      const totalSec = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSec / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      this.timer = `${hours.toString().padStart(2, '0')}h : ${minutes.toString().padStart(2, '0')}m : ${seconds.toString().padStart(2, '0')}s`;
    };

    deferViewUpdate(this.cdr, tick);
    this.timerSubscription = interval(1000).subscribe(() => deferViewUpdate(this.cdr, tick));
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('Copiado en portapapeles', '', {
        duration: 1000,
      });
    });
  }

  goBack(): void {
    switch (this.estado) {
      case BreBEstadoEnum.BREB_METHOD_SELECTION:
        // Volver al paso de selección Bre-b / Tarjeta
        this.selectedBreBMethod = null;
        this.estado = BreBEstadoEnum.SELECTION;
        break;
      case BreBEstadoEnum.QR:
        this.clearQrFlowTimeouts();
        if (this.voucherShownWithoutBreBMethodSelection) {
          this.estado = BreBEstadoEnum.SELECTION;
        } else {
          this.estado = BreBEstadoEnum.BREB_METHOD_SELECTION;
        }
        break;
      case BreBEstadoEnum.PENDING:
        if (this.voucherShownWithoutBreBMethodSelection) {
          this.estado = BreBEstadoEnum.SELECTION;
        } else {
          this.estado = BreBEstadoEnum.BREB_METHOD_SELECTION;
        }
        break;
      case BreBEstadoEnum.APPROVED:
        this.clearQrFlowTimeouts();
        if (this.okUrl) {
          window.location.href = this.okUrl;
        }
        break;
      case BreBEstadoEnum.REFUSED:
      case BreBEstadoEnum.EXPIRED:
        this.clearQrFlowTimeouts();
        if (this.errorUrl) {
          window.location.href = this.errorUrl;
        }
        break;
      default:
        this.clearQrFlowTimeouts();
        this.router.navigate(['/']);
        break;
    }
  }
}

export type MetodoPagoBreB = 'bre-b' | 'tarjeta';

